import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Session-cookie auth. NO tokens in localStorage — the httpOnly cookie is the only credential,
// and it rides along on every same-origin request (we pass credentials:'include' to be explicit).
// The app bootstraps from GET /api/auth/me on load.

const AuthContext = createContext(null);
const EMPTY = { user: null, org: null, plan: null, role: null };

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, ...EMPTY });

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/me', { credentials: 'include' });
      const d = await r.json();
      if (d.authenticated) setState({ loading: false, user: d.user, org: d.org, plan: d.plan, role: d.role });
      else setState({ loading: false, ...EMPTY });
    } catch {
      setState({ loading: false, ...EMPTY });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const post = async (path, body) => {
    const r = await fetch(path, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Request failed');
    return d;
  };

  const login = async (email, password) => {
    const d = await post('/api/auth/login', { email, password });
    setState({ loading: false, user: d.user, org: d.org, plan: d.plan, role: d.role });
    return d;
  };

  const signup = async (payload) => {
    const d = await post('/api/auth/signup', payload);
    setState({ loading: false, user: d.user, org: d.org, plan: d.plan, role: d.role });
    return d;
  };

  const logout = async () => {
    try { await post('/api/auth/logout'); } catch { /* clear locally regardless */ }
    setState({ loading: false, ...EMPTY });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
